import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import CompressIcon from "@mui/icons-material/Compress";
import ExpandIcon from "@mui/icons-material/Expand";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterNoneIcon from "@mui/icons-material/FilterNone";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import {Box, Checkbox, Divider, IconButton, InputAdornment, TextField, Tooltip} from "@mui/material";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {Virtuoso} from "react-virtuoso";
import "./style.css";

/**
 * properties for selectMode
 */
export const DEFAULT_SELECT_MODE = "default";
export const SINGLE_SELECT_MODE = "single";
export const HIERARCHICAL_SELECT_MODE = "hierarchical";

const DEFAULT_PATH_SEPARATOR = "+";

const checkPathStartWithList = (pathToCheck, levelPathList, pathSeparator) => {
  for (const path of levelPathList) {
    if (pathToCheck.startsWith(`${path}${pathSeparator}`)) {
      return true;
    }
  }
  return false;
};

const MaterialTree = (
  {
    treeData,
    idKey,
    childrenKey,
    labelKey,
    renderNode = null,
    isSelectableKey = null,
    selectMode = DEFAULT_SELECT_MODE,
    defaultSelectedNodeIds = null,
    disableSelection = null,
    removeDisabledCheckbox = false,
    onNodeSelect = null,
    defaultExpandedNodeIds = null,
    onNodeClick = null,
    height = null,
    hideButtonBar = false,
    hideSearchBar = false,
    pathSeparator = DEFAULT_PATH_SEPARATOR,
    debounceSearchMs = null,
    minSearchString = null,
    showFolderIcon = false
  },
  ref
) => {
  useImperativeHandle(ref, () => ({
    expandAll: () => expandAll(),
    collapseAll: () => collapseAll(),
    selectAll: () => selectAll(),
    deselectAll: () => deselectAll(),
    expandSelected: () => expandSelected(),
    selectedIds: selectedNodeList || []
  }));

  const {t} = useTranslation();

  const inputRef = useRef();
  const treeItemRef = useRef();
  const elementRef = useRef(null);

  const [allData, setAllData] = useState(null);
  const [visibleData, setVisibleData] = useState([]);

  const [allNodeMap, setAllNodeMap] = useState({});
  const [allNodeList, setAllNodeList] = useState([]);

  const [selectedNodeMap, setSelectedNodeMap] = useState(null);
  const [selectedNodeList, setSelectedNodeList] = useState([]);

  const [expandedNodeMap, setExpandedNodeMap] = useState(null);
  const [expandedChildrenCache, setExpandedChildrenCache] = useState({});

  const [parentChildrenOnlyClicked, setParentChildrenOnlyClicked] = useState({});

  const [allLevels, setAllLevels] = useState(null);
  const [visibleLevels, setVisibleLevels] = useState([]);

  const [selectedLevelMap, setSelectedLevelMap] = useState({});

  // function that creates a flat list with data from the whole tree
  const getAllData = useCallback(
    tree => {
      const data = [];

      const traverseTree = (nodes, parent = null) => {
        nodes.forEach(item => {
          const path = parent ? `${parent.path}${pathSeparator}${item[idKey]}` : `${item[idKey]}`;

          const node = {
            ...item,
            parentId: parent ? parent[idKey] : undefined,
            [childrenKey]: null,
            hasChildren: item[childrenKey] && item[childrenKey].length > 0,
            path: path,
            level: parent ? path.split(pathSeparator).length - 1 : 0
          };

          data.push(node);

          if (item[childrenKey]) {
            traverseTree(item[childrenKey], node);
          }
        });
      };

      traverseTree(tree);

      return data;
    },
    [idKey, pathSeparator, childrenKey]
  );

  // funtion that return the list of first level nodes and the associated expandedNodes
  const getFirstLevelDataAndExpandedNodes = useCallback(allData => {
    const firstLevelData = [];
    allData.forEach(data => {
      if (data.level === 0) {
        firstLevelData.push(_.cloneDeep(data));
      }
    });

    return {
      visibleData: firstLevelData,
      expandedNodes: {}
    };
  }, []);

  // funtion that return the list of visible nodes and the associated expandedNodes
  const getVisibleDataAndExpandedNodes = useCallback(
    (allData, idsToShow) => {
      const newVisibleData = [];
      const newExpandedNodes = {};

      allData.forEach(data => {
        if (data.level === 0) {
          newVisibleData.push(_.cloneDeep(data));
          if (data.hasChildren && idsToShow[data[idKey]]) {
            newExpandedNodes[data[idKey]] = true;
          } else {
            delete newExpandedNodes[data[idKey]];
          }
        }
        if (data.hasChildren && idsToShow[data[idKey]]) {
          const children = allData.filter(el => el.parentId === data[idKey]);
          const index = newVisibleData.findIndex(el => el[idKey] === data[idKey]);
          if (index !== -1) {
            newExpandedNodes[newVisibleData[index][idKey]] = true;
            newVisibleData.splice(+index + 1, 0, ...children);
          }
        }
      });

      return {
        visibleData: newVisibleData,
        expandedNodes: newExpandedNodes
      };
    },
    [idKey]
  );

  // function that creates allLevels
  const getAllLevels = useCallback(
    allData => {
      const treeLevelMap = {};

      allData.forEach(data => {
        if (treeLevelMap[data.level] === undefined) {
          treeLevelMap[data.level] = {
            level: data.level,
            parentLevel: data.level > 0 ? data.level - 1 : null,
            total: data[isSelectableKey] ? 1 : 0,
            expanded: false,
            checked: false,
            hasChildren: true
          };
        } else {
          if (data[isSelectableKey]) {
            treeLevelMap[data.level].total++;
          }
        }
      });

      const treeLevelList = Object.values(treeLevelMap).sort((a, b) => a.level - b.level);
      treeLevelList[treeLevelList.length - 1].hasChildren = false;

      return treeLevelList;
    },
    [isSelectableKey]
  );

  // function that update visibleTreeLevels and treeLevelSelectedNodeMap
  const updateSelectedLevelMap = useCallback(
    selectedNodeIds => {
      if (allLevels) {
        const newSelectedLevelMap = {};

        if (selectedNodeIds) {
          const levelSelectedCount = {};
          allData.forEach(data => {
            if ((selectedNodeIds || []).includes(data[idKey])) {
              if (levelSelectedCount[data.level] === undefined) {
                levelSelectedCount[data.level] = 1;
              } else {
                levelSelectedCount[data.level]++;
              }
            }
          });
          allLevels.forEach(({level, total}) => {
            newSelectedLevelMap[level] = levelSelectedCount[level] === total;
          });
        } else {
          allLevels.forEach(({level}) => {
            newSelectedLevelMap[level] = true;
          });
        }

        setSelectedLevelMap(newSelectedLevelMap);
      }
    },
    [allData, idKey, allLevels]
  );

  // calculate allData and allLevels and initialize allNodeMap and allNodeList
  useEffect(() => {
    if (treeData) {
      if (inputRef?.current) {
        inputRef.current.value = "";
      }

      const allData = getAllData(treeData);
      setAllData(allData);

      if (selectMode !== SINGLE_SELECT_MODE) {
        const allLevels = getAllLevels(allData);
        const visibleLevel = allLevels[0];
        setAllLevels(allLevels);
        setVisibleLevels([visibleLevel]);
      }

      // preparing variables with all selected nodes to be set in case of "select all"
      const newAllSelectedNodes = {};
      const newAllSelectedNodesList = [];
      allData.forEach(data => {
        if (data[isSelectableKey]) {
          newAllSelectedNodes[data[idKey]] = true;
          newAllSelectedNodesList.push(data[idKey]);
        }
      });
      setAllNodeMap(newAllSelectedNodes);
      setAllNodeList(newAllSelectedNodesList);

      setExpandedNodeMap(null);
      setSelectedNodeMap(null);
      setSelectedLevelMap({});
    }
  }, [treeData, getAllData, selectMode, getAllLevels, idKey, isSelectableKey]);

  // calculate initial visible tree
  useEffect(() => {
    if (allData && !expandedNodeMap) {
      if (defaultExpandedNodeIds) {
        const idsToShow = {};
        defaultExpandedNodeIds.forEach(nodeId => {
          const node = allData.find(data => data[idKey] === nodeId);
          if (node) {
            const pathParts = node.path.split(pathSeparator);
            pathParts.forEach(pathPart => {
              idsToShow[pathPart] = true;
            });
          }
        });
        const {visibleData, expandedNodes} = getVisibleDataAndExpandedNodes(allData, idsToShow);
        setVisibleData(visibleData);
        setExpandedNodeMap(expandedNodes);
        setExpandedChildrenCache({});
      } else {
        const {visibleData, expandedNodes} = getFirstLevelDataAndExpandedNodes(allData);
        setVisibleData(visibleData);
        setExpandedNodeMap(expandedNodes);
        setExpandedChildrenCache({});
      }
    }
  }, [
    allData,
    defaultExpandedNodeIds,
    expandedNodeMap,
    idKey,
    pathSeparator,
    getVisibleDataAndExpandedNodes,
    getFirstLevelDataAndExpandedNodes
  ]);

  // initialize selectedNodes and selectedNodeIds
  useEffect(() => {
    if (allData && !selectedNodeMap) {
      const initialSelectedNodes = {};
      (defaultSelectedNodeIds ?? []).forEach(nodeId => {
        initialSelectedNodes[nodeId] = true;
      });
      setSelectedNodeMap(initialSelectedNodes);
      setSelectedNodeList(defaultSelectedNodeIds);
      updateSelectedLevelMap(defaultSelectedNodeIds ?? []);
    }
  }, [allData, defaultSelectedNodeIds, selectedNodeMap, updateSelectedLevelMap]);

  const onSearch = _.debounce(searchString => {
    if (searchString && searchString.trim().length > (minSearchString ?? 1)) {
      const searchStringRegex = new RegExp(`${searchString.toLowerCase()}`);

      const matchedNodeIds = [];
      allData.forEach(data => {
        if (searchStringRegex.test((data[labelKey] ?? "").toLowerCase())) {
          matchedNodeIds.push(...data.path.split(pathSeparator));
        }
      });

      const filteredData = [];
      const newExpandedNodes = _.cloneDeep(expandedNodeMap);
      allData.forEach(data => {
        if (matchedNodeIds.includes(data[idKey])) {
          filteredData.push({...data});
          if (data.hasChildren) {
            newExpandedNodes[data[idKey]] = true;
          } else {
            delete newExpandedNodes[data[idKey]];
          }
        }
      });
      setVisibleData(filteredData);
      setExpandedNodeMap(newExpandedNodes);
    } else {
      resetSearch(true);
    }
  }, debounceSearchMs ?? 400);

  const resetSearch = useCallback(
    keepInputRef => {
      if (!keepInputRef) {
        inputRef.current.value = "";
      }
      const {visibleData, expandedNodes} = getFirstLevelDataAndExpandedNodes(allData);
      setVisibleData(visibleData);
      setExpandedNodeMap(expandedNodes);
      setExpandedChildrenCache({});
    },
    [allData, getFirstLevelDataAndExpandedNodes]
  );

  const selectAll = useCallback(() => {
    setSelectedNodeMap(allNodeMap);
    setSelectedNodeList(allNodeList);
    updateSelectedLevelMap();

    if (onNodeSelect) {
      onNodeSelect(allNodeList);
    }
  }, [allNodeMap, allNodeList, onNodeSelect, updateSelectedLevelMap]);

  const deselectAll = useCallback(() => {
    setSelectedNodeMap({});
    setSelectedNodeList([]);
    setSelectedLevelMap({});

    if (onNodeSelect) {
      onNodeSelect([]);
    }
  }, [onNodeSelect]);

  const selectNode = useCallback(
    (event, index, node) => {
      const newSelectedNodes = selectMode === SINGLE_SELECT_MODE ? {} : _.cloneDeep(selectedNodeMap);

      const checked = event.target.checked;
      if (selectMode === HIERARCHICAL_SELECT_MODE) {
        const pathNodeIds = node.path.split(pathSeparator);
        allData.forEach(data => {
          if (checked) {
            if (data[idKey] === node[idKey] || data.path.startsWith(`${node.path}${pathSeparator}`)) {
              newSelectedNodes[data[idKey]] = true;
            }
          } else {
            if (pathNodeIds.includes(data[idKey]) || data.path.startsWith(`${node.path}${pathSeparator}`)) {
              delete newSelectedNodes[data[idKey]];
            }
          }
        });
      } else if (selectMode === SINGLE_SELECT_MODE) {
        newSelectedNodes[node[idKey]] = true;
      } else {
        if (checked) {
          newSelectedNodes[node[idKey]] = true;
        } else {
          delete newSelectedNodes[node[idKey]];
        }
      }

      setSelectedNodeMap(newSelectedNodes);

      const newSelectedNodeIds = Object.keys(newSelectedNodes);
      setSelectedNodeList(newSelectedNodeIds);
      updateSelectedLevelMap(newSelectedNodeIds);

      if (onNodeSelect) {
        onNodeSelect(newSelectedNodeIds, node, index);
      }
    },
    [selectMode, allData, idKey, pathSeparator, selectedNodeMap, onNodeSelect, updateSelectedLevelMap]
  );

  const expandAll = useCallback(() => {
    const newVisibleData = [];
    const newExpandedNodes = {};

    allData.forEach(fd => {
      newVisibleData.push({...fd});
      if (fd.hasChildren) {
        newExpandedNodes[fd[idKey]] = true;
      }
    });

    setVisibleData(newVisibleData);
    setExpandedNodeMap(newExpandedNodes);
    setExpandedChildrenCache({});
  }, [allData, idKey]);

  const collapseAll = useCallback(() => {
    const {visibleData, expandedNodes} = getFirstLevelDataAndExpandedNodes(allData);

    setVisibleData(visibleData);
    setExpandedNodeMap(expandedNodes);
    setExpandedChildrenCache({});
  }, [allData, getFirstLevelDataAndExpandedNodes]);

  const expandNode = useCallback(
    (index, node) => {
      const newExpandedNodes = _.cloneDeep(expandedNodeMap);

      const nodeId = node[idKey];
      const isExpanding = !expandedNodeMap[nodeId];

      if (isExpanding) {
        newExpandedNodes[nodeId] = true;

        const children = expandedChildrenCache[nodeId]
          ? expandedChildrenCache[nodeId]
          : allData.filter(data => data.parentId === nodeId);
        const newVisibleData = _.cloneDeep(visibleData);
        newVisibleData.splice(+index + 1, 0, ...children);

        setVisibleData(newVisibleData);
        setExpandedChildrenCache({...expandedChildrenCache, [nodeId]: undefined});
      } else {
        delete newExpandedNodes[nodeId];

        const newVisibleData = [];
        const nodesToRemove = [];
        visibleData.forEach(data => {
          if (data.path.startsWith(`${node.path}${pathSeparator}`)) {
            nodesToRemove.push(data);
          } else {
            newVisibleData.push(data);
          }
        });

        setVisibleData(newVisibleData);
        setExpandedChildrenCache({...expandedChildrenCache, [nodeId]: nodesToRemove});
      }
      setExpandedNodeMap(newExpandedNodes);
    },
    [allData, visibleData, idKey, pathSeparator, expandedChildrenCache, expandedNodeMap]
  );

  const expandSelected = () => {
    const idsToShow = {};
    allData.forEach(data => {
      if (selectedNodeMap?.[data[idKey]]) {
        const pathParts = data.path.split(pathSeparator);
        pathParts.forEach(pathPart => {
          idsToShow[pathPart] = true;
        });
      }
    });

    const {visibleData, expandedNodes} = getVisibleDataAndExpandedNodes(allData, idsToShow);
    setVisibleData(visibleData);
    setExpandedNodeMap(expandedNodes);
    setExpandedChildrenCache({});
  };

  const selectAllNodeChildren = useCallback(
    (index, node) => {
      const newSelectedNodes = _.cloneDeep(selectedNodeMap);
      allData.forEach(data => {
        // if node is already in parentChildrenOnlyClicked means that we have to deselect all children, otherwise we have to select them
        if (data.parentId === node[idKey] && !parentChildrenOnlyClicked[node[idKey]]) {
          if (data[isSelectableKey]) {
            newSelectedNodes[data[idKey]] = true;
          }
        } else if (data.parentId === node[idKey] && parentChildrenOnlyClicked[node[idKey]]) {
          delete newSelectedNodes[data[idKey]];
        }
      });

      if (!parentChildrenOnlyClicked[node[idKey]]) {
        setParentChildrenOnlyClicked({...parentChildrenOnlyClicked, [node[idKey]]: true});
      } else {
        const newParentChildrenOnlyClicked = _.cloneDeep(parentChildrenOnlyClicked);
        delete newParentChildrenOnlyClicked[node[idKey]];
        setParentChildrenOnlyClicked({...newParentChildrenOnlyClicked});
      }
      setSelectedNodeMap(newSelectedNodes);

      const newSelectedNodeIds = Object.keys(newSelectedNodes);
      setSelectedNodeList(newSelectedNodeIds);
      updateSelectedLevelMap(newSelectedNodeIds);

      if (onNodeSelect) {
        onNodeSelect(newSelectedNodeIds, node, index);
      }
    },
    [allData, idKey, parentChildrenOnlyClicked, isSelectableKey, selectedNodeMap, onNodeSelect, updateSelectedLevelMap]
  );

  const onTreeLevelCheck = (level, checked, selectMode) => {
    const levelPathList = allData.filter(f => f.level === level).map(f => f.path);

    const newSelectedNodeMap = _.cloneDeep(selectedNodeMap ?? {});
    allData.forEach(node => {
      if (node[isSelectableKey]) {
        if (
          node.level === level ||
          (selectMode === HIERARCHICAL_SELECT_MODE && checkPathStartWithList(node.path, levelPathList, pathSeparator))
        ) {
          if (checked) {
            newSelectedNodeMap[node[idKey]] = true;
          } else {
            delete newSelectedNodeMap[node[idKey]];
          }
        }
      }
    });
    setSelectedNodeMap(newSelectedNodeMap);

    const newSelectedNodeList = Object.keys(newSelectedNodeMap);
    setSelectedNodeList(newSelectedNodeList);

    const newSelectedLevelMap = _.cloneDeep(selectedLevelMap);
    if (checked) {
      newSelectedLevelMap[level] = true;
    } else {
      delete newSelectedLevelMap[level];
    }
    setSelectedLevelMap(newSelectedLevelMap);

    if (onNodeSelect) {
      onNodeSelect(newSelectedNodeList, null, null);
    }
  };

  const onTreeLevelItemExpand = (index, level) => {
    if (allLevels) {
      if (!visibleLevels[index].expanded) {
        const children = allLevels.filter(l => l.parentLevel === level.level);
        const newVisibleTreeLevels = _.cloneDeep(visibleLevels);
        newVisibleTreeLevels[index].expanded = true;
        newVisibleTreeLevels.splice(+index + 1, 0, ...children);
        setVisibleLevels(newVisibleTreeLevels);
      } else {
        const newVisibleTreeLevels = visibleLevels.filter(l => l.level <= level.level);
        newVisibleTreeLevels[index].expanded = false;
        setVisibleLevels(newVisibleTreeLevels);
      }
    }
  };

  const getCheckboxVisibility = useCallback(
    node => {
      if (removeDisabledCheckbox) {
        const isDisabled =
          disableSelection || (selectMode !== HIERARCHICAL_SELECT_MODE && isSelectableKey && !node[isSelectableKey]);
        return !isDisabled;
      } else {
        return true;
      }
    },
    [removeDisabledCheckbox, disableSelection, selectMode, isSelectableKey]
  );

  return (
    allData && (
      <Box className="material-tree__container" sx={{height: "100%", color: theme => theme.palette.text.primary}}>
        <Box className="material-tree__header" ref={elementRef}>
          {(!hideButtonBar || !hideSearchBar || (visibleLevels || []).length > 0) && (
            <>
              <Box className="material-tree__header__toolbar">
                {!hideButtonBar && (
                  <Box className="material-tree__header__toolbar__icons">
                    {selectMode !== SINGLE_SELECT_MODE && (
                      <Tooltip title={t("components.optimizedTree.selectAll.tooltip")}>
                        <span>
                          <IconButton
                            className="material-tree__header__toolbar__icons__icon material-tree__header__toolbar__icons__icon__select-all"
                            disabled={disableSelection}
                            aria-label={t("components.optimizedTree.selectAll.ariaLabel")}
                            onClick={selectAll}
                          >
                            <LibraryAddCheckIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {selectMode !== SINGLE_SELECT_MODE && (
                      <Tooltip title={t("components.optimizedTree.deselectAll.tooltip")}>
                        <span>
                          <IconButton
                            className="material-tree__header__toolbar__icons__icon material-tree__header__toolbar__icons__icon__deselect-all"
                            disabled={disableSelection}
                            aria-label={t("components.optimizedTree.deselectAll.ariaLabel")}
                            onClick={deselectAll}
                          >
                            <FilterNoneIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={t("components.optimizedTree.expandAll.tooltip")}>
                      <span>
                        <IconButton
                          className="material-tree__header__toolbar__icons__icon material-tree__header__toolbar__icons__icon__expand-all"
                          onClick={expandAll}
                          aria-label={t("components.optimizedTree.expandAll.ariaLabel")}
                        >
                          <ExpandIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("components.optimizedTree.collapseAll.tooltip")}>
                      <span>
                        <IconButton
                          className="material-tree__header__toolbar__icons__icon material-tree__header__toolbar__icons__icon__collapse-all"
                          aria-label={t("components.optimizedTree.collapseAll.ariaLabel")}
                          onClick={collapseAll}
                        >
                          <CompressIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
                {!hideSearchBar && (
                  <Box className="material-tree__header__toolbar__search" sx={{width: hideButtonBar ? "100%" : "50%"}}>
                    <TextField
                      id="material-tree__header__search__input"
                      fullWidth
                      inputRef={inputRef}
                      onChange={e => onSearch(e.target.value)}
                      placeholder={t("components.optimizedTree.search.placeholder")}
                      variant="standard"
                      inputProps={{
                        "aria-label": t("components.searchInput.label")
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: inputRef?.current?.value ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => resetSearch(false)}>
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined
                      }}
                    />
                  </Box>
                )}
              </Box>
              {(visibleLevels || []).length > 0 && (
                <Box className="material-tree__header__level-selector">
                  {visibleLevels.map((level, index) => (
                    <Box
                      key={`${level.level}-${index}`}
                      ref={treeItemRef}
                      className={`material-tree__header__level-selector__level material-tree__header__level-selector__level--${level.level}`}
                      sx={{marginLeft: `${level.level * 12 * 2}px`}}
                    >
                      {level.hasChildren ? (
                        <Box
                          className="material-tree__header__level-selector__level__icon"
                          onClick={() => onTreeLevelItemExpand(index, level)}
                        >
                          {level.expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </Box>
                      ) : (
                        <Box className="material-tree__header__level-selector__level__icon-placeholder" />
                      )}
                      <Tooltip
                        title={
                          level.total !== 0 && selectedLevelMap[level.level] === true
                            ? t("components.optimizedTree.levelSelector.uncheckTooltip", {level: level.level})
                            : t("components.optimizedTree.levelSelector.checkTooltip", {level: level.level})
                        }
                      >
                        <Checkbox
                          className="material-tree__header__level-selector__level__checkbox"
                          size="small"
                          sx={{marginRight: "4px"}}
                          checked={level.total !== 0 && selectedLevelMap[level.level] === true}
                          disabled={level.total === 0 || disableSelection}
                          onChange={(_, checked) => onTreeLevelCheck(level.level, checked, selectMode)}
                        />
                      </Tooltip>
                      <span className="material-tree__header__level-selector__level__label">
                        {t("components.optimizedTree.levelSelector.level", {level: level.level})}
                      </span>
                    </Box>
                  ))}
                </Box>
              )}
              <Divider className="material-tree__header__divider" sx={{marginBottom: "8px"}} />
            </>
          )}
        </Box>
        <Box className="material-tree">
          <Virtuoso
            data={visibleData}
            totalCount={allData.length}
            itemContent={(index, node) => {
              return (
                <Box
                  id={`material-tree__node-${node[idKey]}`}
                  className="material-tree__node"
                  sx={{
                    marginLeft: `${node.level * 12 * 2}px`
                  }}
                >
                  {node.hasChildren ? (
                    <Box
                      id={`material-tree__node-${node[idKey]}__icon`}
                      className="material-tree__node__icon"
                      onClick={() => expandNode(index, node)}
                    >
                      {expandedNodeMap?.[node[idKey]] || false ? (
                        <>
                          <ExpandMoreIcon fontSize="small" />
                          {showFolderIcon && <FolderOpenIcon sx={{marginTop: "-2px"}} />}
                        </>
                      ) : (
                        <>
                          <ChevronRightIcon fontSize="small" />
                          {showFolderIcon && <FolderIcon sx={{marginTop: "-2px"}} />}
                        </>
                      )}
                    </Box>
                  ) : (
                    <>
                      <Box
                        id={`material-tree__node-${node[idKey]}__icon-placeholder`}
                        className="material-tree__node__icon-placeholder"
                      />
                      {showFolderIcon && <FolderOpenIcon sx={{marginTop: "-2px"}} />}
                    </>
                  )}
                  {getCheckboxVisibility(node) && (
                    <Tooltip
                      title={
                        selectedNodeMap?.[node[idKey]]
                          ? t("components.optimizedTree.checkBox.uncheck.tooltip")
                          : t("components.optimizedTree.checkBox.check.tooltip")
                      }
                    >
                      <Checkbox
                        id={`material-tree__node-${node[idKey]}__checkbox`}
                        className="material-tree__node__checkbox"
                        size="small"
                        sx={{marginRight: "2px"}}
                        checked={selectedNodeMap?.[node[idKey]] || false}
                        onChange={e => selectNode(e, index, node)}
                        checkedIcon={selectMode === SINGLE_SELECT_MODE ? <RadioButtonCheckedIcon /> : undefined}
                        icon={selectMode === SINGLE_SELECT_MODE ? <RadioButtonUncheckedIcon /> : undefined}
                        disabled={
                          disableSelection ||
                          (selectMode !== HIERARCHICAL_SELECT_MODE && isSelectableKey && !node[isSelectableKey])
                        }
                        slotProps={{
                          input: {
                            "aria-label": selectedNodeMap?.[node[idKey]]
                              ? t("components.optimizedTree.checkBox.uncheck.tooltip")
                              : t("components.optimizedTree.checkBox.check.tooltip")
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  <Box
                    component="span"
                    id={`material-tree__node-${node[idKey]}__label`}
                    className="material-tree__node__label"
                    sx={{
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      if (onNodeClick) onNodeClick(node, index);
                      expandNode(index, node);
                    }}
                  >
                    {renderNode ? renderNode(node) : node[labelKey]}
                  </Box>
                  {!disableSelection && selectMode === DEFAULT_SELECT_MODE && node.hasChildren && (
                    <Tooltip title={t("components.optimizedTree.actions.selectChildren.title")}>
                      <IconButton
                        id={`material-tree__node-${node[idKey]}__select-children`}
                        className="material-tree__node__select-children"
                        style={{marginLeft: "4px"}}
                        aria-label={t("components.optimizedTree.actions.selectChildren.ariaLabel")}
                        size="small"
                        onClick={() => selectAllNodeChildren(index, node)}
                      >
                        <PlaylistAddCheckIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            }}
            computeItemKey={(_, node) => node.path + (expandedNodeMap?.[node[idKey]] || false ? "_expanded" : "")}
            style={{height: height ?? "100%"}}
          />
        </Box>
      </Box>
    )
  );
};

export default forwardRef(MaterialTree);
